import React from 'react';

import { Form, Select, InputNumber, Row, Col } from 'antd';

interface PaperSizeOption {
  label: string;
  value: string;
}

interface PaperSizeFormItemsProps {
  paperSizeOptions: PaperSizeOption[];
  showCustomInputs?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  modelName?: string;
  namespace?: string;
}

/**
 * PaperSizeFormItems - Reusable form items for paper size selection
 *
 * @param props.paperSizeOptions - Paper size options [{ label, value }]
 * @param props.showCustomInputs - Whether to show custom width/height inputs
 * @param props.t - i18n translate function
 * @param props.modelName - Model name for i18n keys (e.g., 'order_template', 'product_label_template')
 * @param props.namespace - i18n namespace (e.g., 'shared.order_templates', 'shared.product_label_templates')
 */
const PaperSizeFormItems: React.FC<PaperSizeFormItemsProps> = ({
  paperSizeOptions,
  showCustomInputs = false,
  t,
  modelName = 'order_template',
  namespace = 'shared.order_templates'
}) => {
  return (
    <>
      {/* Paper Size Type Selection */}
      <Form.Item
        label={t(`models.${modelName}.paper_size_type`)}
        name="paper_size_type"
        rules={[{ required: true, message: t(`${namespace}.form.paper_size_type_required`) }]}
      >
        <Select
          placeholder={t(`${namespace}.form.paper_size_type_placeholder`)}
          options={paperSizeOptions}
        />
      </Form.Item>

      {/* Custom Size Inputs (only when custom is selected) */}
      {showCustomInputs && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t(`models.${modelName}.paper_width`)}
              name="paper_width"
              rules={[
                { required: true, message: t(`${namespace}.form.paper_width_required`) },
                { type: 'number', min: 1, max: 999999, message: t(`${namespace}.form.paper_size_invalid`) }
              ]}
            >
              <InputNumber
                placeholder={t(`${namespace}.form.paper_width_placeholder`)}
                addonAfter="mm"
                min={1}
                max={999999}
                step={1}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t(`models.${modelName}.paper_height`)}
              name="paper_height"
              rules={[
                { required: true, message: t(`${namespace}.form.paper_height_required`) },
                { type: 'number', min: 1, max: 999999, message: t(`${namespace}.form.paper_size_invalid`) }
              ]}
            >
              <InputNumber
                placeholder={t(`${namespace}.form.paper_height_placeholder`)}
                addonAfter="mm"
                min={1}
                max={999999}
                step={1}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};

export default PaperSizeFormItems;
